import React, { useState, useEffect, useContext } from "react";
import { FaCircle } from "react-icons/fa";
import PropTypes from "prop-types";
import { useNavigate } from "react-router";
import config from "../../../../config";
import WriteMessage from "./WriteMessage";
import { AuthContext } from "../../../../context/AuthContext";
import { useSession } from "../../../../context/SessionTracker";

const ShareToMessages = React.memo(({
  setGenerateLinkNow,
  generatedLink,
  generatedElements,
  isLoadingImage,
  selectedImageIndex,
  setSelectedImageIndex,
  linkOrWishlist,
  setShowDownloadBtn,
  details,
}) => {
  const [showMessageSteps, setShowMessageSteps] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const { user } = useContext(AuthContext);
  const [CTA, setCTA] = useState("");
  const [fullCTA, setFullCTA] = useState("");
  const navigate = useNavigate();
  const { trackEvent } = useSession();

  useEffect(() =>{
    if (details.giveorreceive === "give" && details.type === "currency") {
      // 💸 
      setCTA(`Let me send you some bread \u{1F4B8} `);
    }
    if (details.giveorreceive === "receive" && details.type === "currency") {
      // 💸 
      setCTA(`Wanna give me $${details.amount}? \u{1F4B8} `);
    }
    if (details.giveorreceive === "give" && details.type === "item") {
      // 🎁
      setCTA(`Do you want this? -- ${details.title} \u{1F381} `);
    }
    if (details.giveorreceive === "receive" && details.type === "item") {
      // 💕
      setCTA(`I need this rn \u{1F495} `);
    }
    if (linkOrWishlist === "wishlist"){
      // 🎁
      setCTA(`Make my day, send me something on my wishlist \u{1F381} `);

    } 
  }, [generatedLink])
  
  useEffect(() => {
    if (currentStep != 0) {
      setShowDownloadBtn(false);
    } else {
      setShowDownloadBtn(true);
    }
  }, [currentStep]);


  // Handle sharing via text message
  const shareToTextMessage = () => {
    setShowMessageSteps(true);

    // increment step counter
    setCurrentStep((prev) => {
      const nextStep = prev + 1;
      if(nextStep === 1){
        const urlToShare = `${config.publicUrl}/${linkOrWishlist}/${user.username}`;
        const message = CTA + "\n\n" + urlToShare;
        setFullCTA(message);
      }
      if (nextStep === 2) {
        try {
          const encodedText = encodeURIComponent(fullCTA);
          trackEvent("linkShare", details, {
            destination: "messages",
            user_agent: window.navigator.userAgent,
          });
          navigate("/");
          // Open the text message app (this example is for iOS)
          window.location.href = `sms:?&body=${encodedText}`;
        } catch (error) {
          console.error("Could not share to messages.");
        }
      }
      return nextStep < stepInstructions.length ? nextStep : prev; // Prevent incrementing beyond array length
    });
  };

  useEffect(() => {
    const container = document.querySelector(".image-selection-container");
    if (!container) return;
    let frameId = null; // To hold the requestAnimationFrame ID
    
    setGenerateLinkNow(true);
    
    // Define the scroll event listener function
    const onScroll = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        let closestIndex = 0;
        let closestDistance = Infinity;
        const thumbnails = document.querySelectorAll(".thumbnail"); // Move inside the event listener if thumbnails might change

        thumbnails.forEach((thumbnail, index) => {
          const scrollPosition =
            container.scrollLeft + container.offsetWidth / 2;
          const thumbnailCenter =
            thumbnail.offsetLeft + thumbnail.offsetWidth / 2;
          const distance = Math.abs(scrollPosition - thumbnailCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        thumbnails.forEach((thumbnail, index) => {
          if (index === closestIndex) {
            thumbnail.classList.add("active");
            setSelectedImageIndex(index);
          } else {
            thumbnail.classList.remove("active");
          }
        });
      });
    };

    // Add the event listener
    container.addEventListener("scroll", onScroll);

    return () => {
      container.removeEventListener("scroll", onScroll);
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [generatedElements]);

  const stepImages = [
    null,
    <WriteMessage key="step-2" details={details} CTA={fullCTA} />,
  ];

  const stepInstructions = [
    "Optional: download image",
    "Click share to go to messages",
  ];

  return (
    <>
      {showMessageSteps && !isLoadingImage && generatedElements && (
        <div className="insta-steps-container">
          <div className="progress-circles-container">
            {stepImages.map((_, index) => (
              <FaCircle
                key={index}
                className={`circle-icon ${
                  index <= currentStep ? "active" : ""
                }`}
                onClick={() => setCurrentStep(index)}
              />
            ))}
          </div>
          <div className="step-descriptions-container">
            <p>{stepInstructions[currentStep]}</p>
          </div>

          <div className="steps-and-images-container">
            <div className="image-row-container">
              {/* Selected Image */}
              {currentStep > 0 && (
                <div className="selected-image-container">
                    {stepImages[currentStep]}
                </div>
              )}

              {/* Thumbnails */}
              <div
                className={`image-selection-container ${
                  currentStep === 0 ? "with-padding" : ""
                }`}
              >
                {currentStep === 0 &&
                  generatedElements.length > 0 &&
                  generatedElements.map((element, index) => (
                    <div
                      key={index}
                      id={`thumbnail-container-${index}`}
                      className={`thumbnail ${
                        selectedImageIndex === index ? "active" : ""
                      }`}
                      onClick={() => {
                        setSelectedImageIndex(index);
                        // scrollToThumbnail(index)
                      }}
                    >
                      {element}
                    </div>
                  ))}
              </div>
            </div>
          </div>
          {/* alert and button container */}
          <div className="alert-and-button">
            <button
              className="share-button"
              type="button"
              onClick={shareToTextMessage}
            >
              {currentStep === 1 ? "Share!" : "Next Step"}
            </button>
          </div>
        </div>
      )}
    </>
  );
})

ShareToMessages.displayName = "ShareToMessages";

export default ShareToMessages;

ShareToMessages.propTypes = {
  setGenerateLinkNow: PropTypes.func,
  generatedLink: PropTypes.object,
  generatedElements: PropTypes.array,
  isLoadingImage: PropTypes.bool,
  downloadImage: PropTypes.func,
  selectedImageIndex: PropTypes.number,
  setSelectedImageIndex: PropTypes.func,
  details: PropTypes.object,
  linkOrWishlist: PropTypes.string,
  setShowDownloadBtn: PropTypes.func,
  setIsLastStep: PropTypes.func
};
